import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
    ArrowLeft, 
    Heart,
    X,
    ChevronRight,
    Check,
    Users,
    Wind,
    Hand,
    Copy,
    Loader2,
    Sparkles,
    Link2,
    RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { IconButton, PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard, SelectionCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Partner Mode - Real-time Co-Regulation for Couples
 * 
 * A gentle, trauma-informed tool that allows two partners to:
 * - Slow down during conflict or disconnection
 * - Privately process their emotions
 * - Share only safe, curated information
 * - Respond with guided, soothing phrases
 * - Ground together
 * 
 * SAFETY: Never shares raw emotional content. Only shares:
 * - Softer emotion (e.g., "scared", "alone")
 * - Need (e.g., "reassurance", "space")
 */

const PartnerMode = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token, user } = useAuth();
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const maxReconnectAttempts = 5;
    
    // Session state
    const [step, setStep] = useState('intro'); // intro, create, join, waiting, grounding, body_check, emotion, need, waiting_partner, received, respond, coregulate, reconnect
    const [sessionCode, setSessionCode] = useState('');
    const [joinCode, setJoinCode] = useState('');
    const [role, setRole] = useState(null); // partner_a or partner_b
    const [partnerName, setPartnerName] = useState('Your partner');
    const [partnerConnected, setPartnerConnected] = useState(false);
    const [partnerStep, setPartnerStep] = useState('waiting');
    const [isLoading, setIsLoading] = useState(false);
    const [wsConnected, setWsConnected] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [partnerCanReconnect, setPartnerCanReconnect] = useState(false);
    const [reconnectGraceMinutes, setReconnectGraceMinutes] = useState(5);
    
    // Private reflection state (never shared directly)
    const [selectedBody, setSelectedBody] = useState(null);
    const [selectedEmotion, setSelectedEmotion] = useState(null);
    const [selectedNeed, setSelectedNeed] = useState(null);
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [selectedGrounding, setSelectedGrounding] = useState(null);
    
    // Received from partner (only safe data)
    const [partnerEmotion, setPartnerEmotion] = useState(null);
    const [partnerNeed, setPartnerNeed] = useState(null);
    const [partnerResponse, setPartnerResponse] = useState(null);
    const [partnerGrounding, setPartnerGrounding] = useState(null);

    // Body check options
    const bodyOptions = [
        { id: 'tense', label: 'I feel tense or activated', icon: '⚡' },
        { id: 'shutdown', label: 'I feel shut down or distant', icon: '🌙' },
        { id: 'hurt', label: 'I feel hurt or scared', icon: '💔' },
        { id: 'overwhelmed', label: 'I feel overwhelmed', icon: '🌊' },
        { id: 'unsure', label: "I'm not sure", icon: '🤷' }
    ];

    // Softer emotion options (this gets shared)
    const emotionOptions = [
        { id: 'alone', label: 'I feel alone', shareLabel: 'alone' },
        { id: 'scared', label: "I feel scared I'm losing you", shareLabel: 'scared' },
        { id: 'overwhelmed', label: 'I feel overwhelmed', shareLabel: 'overwhelmed' },
        { id: 'not-enough', label: 'I feel not good enough', shareLabel: 'not good enough' },
        { id: 'misunderstood', label: 'I feel misunderstood', shareLabel: 'misunderstood' },
        { id: 'disconnected', label: 'I feel disconnected', shareLabel: 'disconnected' },
        { id: 'unsure', label: "I'm not sure", shareLabel: 'unsure' }
    ];

    // Need options (this gets shared)
    const needOptions = [
        { id: 'reassurance', label: 'I need reassurance', shareLabel: 'reassurance' },
        { id: 'breathe', label: 'I need a moment to breathe', shareLabel: 'a moment to breathe' },
        { id: 'heard', label: 'I need to feel heard', shareLabel: 'to feel heard' },
        { id: 'closeness', label: 'I need closeness', shareLabel: 'closeness' },
        { id: 'clarity', label: 'I need clarity', shareLabel: 'clarity' },
        { id: 'gentleness', label: 'I need gentleness', shareLabel: 'gentleness' },
        { id: 'space', label: 'I need space, but not distance', shareLabel: 'space, but not distance' },
        { id: 'unsure', label: "I'm not sure", shareLabel: 'something' }
    ];

    // Safe response options
    const responseOptions = [
        { id: 'here', label: "I'm here." },
        { id: 'thank-you', label: 'Thank you for telling me.' },
        { id: 'okay', label: "We're okay." },
        { id: 'understand', label: 'I want to understand.' },
        { id: 'slow-down', label: "Let's slow down together." },
        { id: 'didnt-realise', label: "I didn't realise you felt that way." }
    ];

    // Co-regulation grounding options
    const groundingOptions = [
        { id: 'breath', label: 'Take one slow breath together.', icon: Wind },
        { id: 'heart', label: 'Place a hand on your heart.', icon: Heart },
        { id: 'closer', label: 'Sit closer if it feels safe.', icon: Users },
        { id: 'touch', label: 'Hold hands if you both want to.', icon: Hand }
    ];

    // WebSocket connection
    const connectWebSocket = useCallback((code, userRole) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            return;
        }

        const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
        const ws = new WebSocket(`${wsUrl}/ws/partner-mode/${code}/${userRole}`);

        ws.onopen = () => {
            console.log('WebSocket connected');
            setWsConnected(true);
            setIsReconnecting(false);
            reconnectAttemptsRef.current = 0;
        };

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            handleWebSocketMessage(data);
        };

        ws.onclose = (event) => {
            console.log('WebSocket closed:', event.code, event.reason);
            setWsConnected(false);
            
            // Attempt reconnect if not intentional close and within retry limits
            if (event.code !== 1000 && sessionCode && reconnectAttemptsRef.current < maxReconnectAttempts) {
                setIsReconnecting(true);
                reconnectAttemptsRef.current += 1;
                const delay = Math.min(3000 * reconnectAttemptsRef.current, 15000); // Exponential backoff, max 15s
                
                toast.info('Connection lost', {
                    description: `Attempting to reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`
                });
                
                reconnectTimeoutRef.current = setTimeout(() => {
                    connectWebSocket(code, userRole);
                }, delay);
            } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
                setIsReconnecting(false);
                toast.error('Could not reconnect', {
                    description: 'Please rejoin the session manually.'
                });
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        wsRef.current = ws;
    }, [sessionCode]);

    const handleWebSocketMessage = (data) => {
        switch (data.type) {
            case 'partner_connected':
                setPartnerConnected(true);
                setPartnerCanReconnect(false);
                setPartnerName(data.partner_name || 'Your partner');
                toast.success(`${data.partner_name || 'Your partner'} has connected`);
                break;
            
            case 'partner_reconnected':
                setPartnerConnected(true);
                setPartnerCanReconnect(false);
                setPartnerName(data.partner_name || 'Your partner');
                toast.success(`${data.partner_name || 'Your partner'} has reconnected`, {
                    icon: <RefreshCw className="w-4 h-4 text-[#1D6B6B]" />
                });
                break;
            
            case 'session_ready':
                setPartnerConnected(true);
                setPartnerCanReconnect(false);
                if (step === 'waiting') {
                    setStep('grounding');
                }
                if (data.reconnected) {
                    toast.success('Session restored', {
                        description: 'You can continue where you left off.'
                    });
                }
                break;
            
            case 'partner_progress':
                setPartnerStep(data.step);
                break;
            
            case 'partner_shared':
                setPartnerEmotion(data.softer_emotion);
                setPartnerNeed(data.need);
                if (step === 'waiting_partner') {
                    setStep('received');
                }
                break;
            
            case 'partner_response':
                setPartnerResponse(data.response);
                toast.success(`${data.partner_name} responded`, {
                    description: data.response,
                    icon: <Heart className="w-4 h-4 text-[#FFBB98]" />
                });
                break;
            
            case 'partner_grounding':
                setPartnerGrounding(data.choice);
                break;
            
            case 'partner_disconnected':
                setPartnerConnected(false);
                setPartnerCanReconnect(data.can_reconnect || false);
                if (data.reconnect_grace_minutes) {
                    setReconnectGraceMinutes(data.reconnect_grace_minutes);
                }
                toast.warning('Your partner has disconnected', {
                    description: data.can_reconnect 
                        ? `They have ${data.reconnect_grace_minutes || 5} minutes to reconnect.`
                        : 'They can rejoin using the same code.',
                    icon: <RefreshCw className="w-4 h-4 text-[#FFBB98]" />
                });
                break;
            
            case 'session_complete':
                toast.success(data.message, {
                    icon: <Sparkles className="w-4 h-4 text-[#7D8E95]" />
                });
                break;
            
            default:
                break;
        }
    };

    const sendWebSocketMessage = (type, payload = {}) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type, ...payload }));
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (wsRef.current) {
                wsRef.current.close(1000, 'Component unmounted');
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, []);

    // Check for join code in URL
    useEffect(() => {
        const codeFromUrl = searchParams.get('code');
        if (codeFromUrl) {
            setJoinCode(codeFromUrl.toUpperCase());
            setStep('join');
        }
    }, [searchParams]);

    // API calls
    const createSession = async () => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `${API_URL}/api/partner-mode/create`,
                { user_name: user?.name || 'Partner A' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSessionCode(response.data.session_code);
            setRole('partner_a');
            setStep('waiting');
            connectWebSocket(response.data.session_code, 'partner_a');
        } catch (error) {
            toast.error('Could not create session', {
                description: error.response?.data?.detail || 'Please try again'
            });
        }
        setIsLoading(false);
    };

    const joinSession = async () => {
        if (!joinCode || joinCode.length < 4) {
            toast.error('Please enter a valid code');
            return;
        }
        
        setIsLoading(true);
        try {
            const response = await axios.post(
                `${API_URL}/api/partner-mode/join`,
                { session_code: joinCode.toUpperCase(), user_name: user?.name || 'Partner B' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSessionCode(joinCode.toUpperCase());
            setRole('partner_b');
            setPartnerName(response.data.partner_a_name || 'Your partner');
            setStep('waiting');
            connectWebSocket(joinCode.toUpperCase(), 'partner_b');
        } catch (error) {
            toast.error('Could not join session', {
                description: error.response?.data?.detail || 'Please check the code'
            });
        }
        setIsLoading(false);
    };

    const endSession = async () => {
        try {
            if (sessionCode) {
                await axios.delete(
                    `${API_URL}/api/partner-mode/${sessionCode}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
        } catch (error) {
            // Ignore errors on cleanup
        }
        if (wsRef.current) {
            wsRef.current.close(1000, 'Session ended');
        }
        navigate('/regulate/coregulation');
    };

    const copySessionLink = () => {
        const link = `${window.location.origin}/regulate/partner-mode?code=${sessionCode}`;
        navigator.clipboard.writeText(link);
        toast.success('Link copied!', {
            description: 'Share this with your partner'
        });
    };

    const copySessionCode = () => {
        navigator.clipboard.writeText(sessionCode);
        toast.success('Code copied!');
    };

    // Step handlers
    const handleBodySelect = (option) => {
        setSelectedBody(option);
        sendWebSocketMessage('update_state', { state: { step_completed: 'body_check' } });
    };

    const handleEmotionSelect = (option) => {
        setSelectedEmotion(option);
        sendWebSocketMessage('update_state', { state: { step_completed: 'emotion', softer_emotion: option.shareLabel } });
    };

    const handleNeedSelect = (option) => {
        setSelectedNeed(option);
        sendWebSocketMessage('update_state', { state: { step_completed: 'need', need: option.shareLabel } });
    };

    const handleShareWithPartner = () => {
        sendWebSocketMessage('share_safe_state');
        sendWebSocketMessage('update_state', { state: { step_completed: 'ready_to_share' } });
        
        // Check if partner already shared
        if (partnerEmotion && partnerNeed) {
            setStep('received');
        } else {
            setStep('waiting_partner');
        }
    };

    const handleSendResponse = (option) => {
        setSelectedResponse(option);
        sendWebSocketMessage('send_response', { response: option.label });
        setStep('coregulate');
    };

    const handleGroundingSelect = (option) => {
        setSelectedGrounding(option);
        sendWebSocketMessage('grounding_choice', { choice: option.id });
    };

    const handleComplete = () => {
        sendWebSocketMessage('complete');
        setStep('reconnect');
    };

    const handleClose = () => {
        endSession();
    };

    const handleBack = () => {
        const stepOrder = ['intro', 'create', 'join', 'waiting', 'grounding', 'body_check', 'emotion', 'need', 'waiting_partner', 'received', 'respond', 'coregulate', 'reconnect'];
        const currentIndex = stepOrder.indexOf(step);
        
        if (currentIndex <= 0 || step === 'intro' || step === 'create' || step === 'join') {
            handleClose();
        } else if (step === 'waiting') {
            handleClose();
        } else if (currentIndex > 0) {
            setStep(stepOrder[currentIndex - 1]);
        }
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Breathing animation */}
            <style>{`
                @keyframes breathe {
                    0%, 100% { transform: scale(1); opacity: 0.7; }
                    50% { transform: scale(1.15); opacity: 1; }
                }
                @keyframes pulse-soft {
                    0%, 100% { opacity: 0.4; }
                    50% { opacity: 0.8; }
                }
            `}</style>

            {/* Header */}
            <header className="sticky top-0 z-50 glass-header">
                <Container size="sm" className="py-5 flex items-center justify-between">
                    <IconButton 
                        onClick={handleBack}
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <div className="text-center">
                        <Heading as="h1" size="text-lg">Partner Mode</Heading>
                        {isReconnecting ? (
                            <div className="flex items-center justify-center gap-1">
                                <Loader2 className="w-3 h-3 animate-spin text-[#FFBB98]" />
                                <SmallText className="text-[#FFBB98]">Reconnecting...</SmallText>
                            </div>
                        ) : wsConnected ? (
                            <SmallText className="text-[#1D6B6B]">Connected</SmallText>
                        ) : sessionCode ? (
                            <SmallText className="text-[#7D8E95]">Disconnected</SmallText>
                        ) : null}
                    </div>
                    <IconButton 
                        onClick={handleClose}
                        data-testid="close-btn"
                        className="hover:bg-[#FFBB98]/10"
                    >
                        <X className="w-5 h-5" />
                    </IconButton>
                </Container>
            </header>

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* INTRO */}
                {step === 'intro' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-10">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Users className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-4">
                                Partner Mode
                            </Heading>
                        </div>

                        <div className="glass-card rounded-2xl p-5">
                            <BodyText className="text-[#1D6B6B] leading-relaxed text-center text-lg">
                                Partner Mode helps two people slow down, understand each other's internal experience, and reconnect with softness.
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-8">
                            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#7D8E95]/10">
                                <Check className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-0.5" />
                                <SmallText className="text-[#2A8585]">
                                    Each partner uses their own device
                                </SmallText>
                            </div>
                            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#7D8E95]/10">
                                <Check className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-0.5" />
                                <SmallText className="text-[#2A8585]">
                                    You choose what to share
                                </SmallText>
                            </div>
                            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#7D8E95]/10">
                                <Check className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-0.5" />
                                <SmallText className="text-[#2A8585]">
                                    Only gentle, safe information is shared
                                </SmallText>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <PrimaryButton
                                onClick={() => setStep('create')}
                                className="w-full"
                                size="xl"
                                data-testid="start-partner-mode"
                            >
                                Start Partner Mode
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>

                        <div className="mt-8 text-center">
                            <SmallText className="text-[#7D8E95]">
                                You can leave at any time. This is entirely optional.
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* CREATE OR JOIN */}
                {step === 'create' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="lavender" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Link2 className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                Connect with your partner
                            </Heading>
                            <BodyText className="text-[#2A8585]">
                                Create a session or join one your partner started.
                            </BodyText>
                        </div>

                        <div className="space-y-4 mb-8">
                            <button
                                className="w-full text-left rounded-2xl p-5 glass-card-sage card-hover transition-all brand-slide-up"
                                onClick={createSession}
                                data-testid="create-session"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                                        style={{ background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', color: 'white' }}>
                                        <Sparkles className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <Heading as="h3" size="text-base" className="mb-1">
                                            Create a session
                                        </Heading>
                                        <SmallText className="text-[#7D8E95]">
                                            Get a code to share with your partner
                                        </SmallText>
                                    </div>
                                    {isLoading ? (
                                        <Loader2 className="w-5 h-5 text-[#1D6B6B] animate-spin" />
                                    ) : (
                                        <ChevronRight className="w-5 h-5 text-[#7D8E95]" />
                                    )}
                                </div>
                            </button>

                            <div className="flex items-center gap-4 py-2">
                                <div className="flex-1 h-px bg-[#FFBB98]/50" />
                                <SmallText className="text-[#7D8E95]">or</SmallText>
                                <div className="flex-1 h-px bg-[#FFBB98]/50" />
                            </div>

                            <div className="glass-card-gold rounded-2xl p-5">
                                <SmallText className="text-[#7D8E95] mb-3 block">
                                    Have a code from your partner?
                                </SmallText>
                                <div className="flex gap-3">
                                    <input
                                        type="text"
                                        value={joinCode}
                                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                        placeholder="Enter code"
                                        maxLength={6}
                                        className="flex-1 px-4 py-3 rounded-xl bg-white/80 border border-white/40/30 text-center text-lg font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-[#2A8585]/50"
                                        data-testid="join-code-input"
                                    />
                                    <PrimaryButton
                                        onClick={joinSession}
                                        disabled={joinCode.length < 4 || isLoading}
                                        data-testid="join-session"
                                    >
                                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Join'}
                                    </PrimaryButton>
                                </div>
                            </div>
                        </div>

                        <SecondaryButton onClick={() => setStep('intro')} className="w-full" size="lg">
                            Back
                        </SecondaryButton>
                    </div>
                )}

                {/* JOIN (from URL) - Welcome screen for Partner B */}
                {step === 'join' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Users className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                Welcome
                            </Heading>
                            <BodyText className="text-[#2A8585] leading-relaxed">
                                You'll each have your own private space to check in before anything is shared.
                            </BodyText>
                        </div>

                        <div className="glass-card rounded-2xl p-5">
                            <SmallText className="text-[#7D8E95] mb-2 block">Session Code</SmallText>
                            <Heading as="h3" size="text-3xl" className="font-mono tracking-wider">
                                {joinCode}
                            </Heading>
                        </div>

                        <div className="space-y-4">
                            <PrimaryButton
                                onClick={joinSession}
                                className="w-full"
                                size="xl"
                                disabled={isLoading}
                                data-testid="confirm-join"
                            >
                                {isLoading ? (
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                ) : null}
                                Join Session
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* WAITING FOR PARTNER - Partner A sees "Invite Your Partner" */}
                {step === 'waiting' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <div 
                                className="w-20 h-20 mx-auto mb-5 rounded-full bg-[#7D8E95]/20 flex items-center justify-center"
                                style={{ animation: partnerConnected ? 'none' : 'pulse-soft 2s ease-in-out infinite' }}
                            >
                                {partnerConnected ? (
                                    <Check className="w-8 h-8 text-[#1D6B6B]" />
                                ) : (
                                    <Users className="w-8 h-8 text-[#7D8E95]" />
                                )}
                            </div>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                {partnerConnected ? 'Partner Connected' : 'Invite Your Partner'}
                            </Heading>
                            <BodyText className="text-[#2A8585]">
                                {partnerConnected 
                                    ? `${partnerName} is here. You can begin when ready.`
                                    : 'Share this link or code so your partner can join on their own device.'
                                }
                            </BodyText>
                        </div>

                        {!partnerConnected && (
                            <>
                                <div className="glass-card rounded-2xl p-5">
                                    <SmallText className="text-[#7D8E95] mb-2 block">Session Code</SmallText>
                                    <Heading as="h3" size="text-3xl" className="font-mono tracking-wider mb-4">
                                        {sessionCode}
                                    </Heading>
                                    <div className="flex gap-3 justify-center">
                                        <TertiaryButton onClick={copySessionCode}>
                                            <Copy className="w-4 h-4 mr-2" />
                                            Copy Code
                                        </TertiaryButton>
                                        <TertiaryButton onClick={copySessionLink}>
                                            <Link2 className="w-4 h-4 mr-2" />
                                            Copy Link
                                        </TertiaryButton>
                                    </div>
                                </div>

                                <div className="p-4 rounded-xl bg-white/70/60 border border-white/40/40 mb-8 text-center">
                                    <SmallText className="text-[#7D8E95]">
                                        Your partner needs to open CoRegulate and enter this code to connect.
                                    </SmallText>
                                </div>
                            </>
                        )}

                        {partnerConnected && (
                            <div className="space-y-4">
                                <PrimaryButton
                                    onClick={() => setStep('grounding')}
                                    className="w-full"
                                    size="xl"
                                    data-testid="begin-session"
                                >
                                    Begin
                                    <ChevronRight className="w-4 h-4 ml-2" />
                                </PrimaryButton>
                            </div>
                        )}

                        <div className="mt-8">
                            <SecondaryButton onClick={handleClose} className="w-full" size="lg">
                                Leave session
                            </SecondaryButton>
                        </div>
                    </div>
                )}

                {/* GROUNDING MOMENT (PRIVATE) */}
                {step === 'grounding' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <div 
                                className="w-20 h-20 mx-auto mb-5 rounded-full bg-[#7D8E95]/30 flex items-center justify-center"
                                style={{ animation: 'breathe 4s ease-in-out infinite' }}
                            >
                                <Wind className="w-8 h-8 text-[#7D8E95]" />
                            </div>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                Take a Moment
                            </Heading>
                            <BodyText className="text-[#2A8585] leading-relaxed">
                                Let your shoulders drop. You're safe here.
                            </BodyText>
                        </div>

                        <PrimaryButton
                            onClick={() => setStep('body_check')}
                            className="w-full"
                            size="xl"
                            data-testid="continue-grounding"
                        >
                            I'm ready
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </PrimaryButton>
                    </div>
                )}

                {/* BODY CHECK-IN (PRIVATE) */}
                {step === 'body_check' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="blue" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Sparkles className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                What's happening in your body?
                            </Heading>
                        </div>

                        <div className="p-3 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mb-6 text-center">
                            <SmallText className="text-[#7D8E95]">
                                This is private — only you can see this
                            </SmallText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {bodyOptions.map((option, index) => {
                                const cardVariants = ['glass-card-sage', 'glass-card-rose', 'glass-card-lavender', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedBody?.id === option.id ? 'ring-2 ring-[#7D8E95]' : ''}`}
                                        style={{ animationDelay: `${index * 60}ms` }}
                                        onClick={() => handleBodySelect(option)}
                                        data-testid={`body-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <span className="text-xl">{option.icon}</span>
                                            <BodyText className="text-[#1D6B6B] flex-1">
                                                {option.label}
                                            </BodyText>
                                            {selectedBody?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#7D8E95]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="p-4 rounded-xl bg-white/70/60 border border-white/40/40 mb-8">
                            <SmallText className="text-[#7D8E95] leading-relaxed text-center">
                                It's okay not to know.
                            </SmallText>
                        </div>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton 
                                onClick={() => setStep('emotion')} 
                                className="flex-1" 
                                size="lg"
                                disabled={!selectedBody}
                            >
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* SOFTER EMOTION (PRIVATE - will be shared) */}
                {step === 'emotion' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                What's underneath that?
                            </Heading>
                        </div>

                        <div className="p-3 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mb-6 text-center">
                            <SmallText className="text-[#1D6B6B]">
                                This will be shared gently with your partner
                            </SmallText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {emotionOptions.map((option, index) => {
                                const cardVariants = ['glass-card-rose', 'glass-card-sage', 'glass-card-lavender', 'glass-card-gold', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedEmotion?.id === option.id ? 'ring-2 ring-[#FFBB98]' : ''}`}
                                        style={{ animationDelay: `${index * 50}ms` }}
                                        onClick={() => handleEmotionSelect(option)}
                                        data-testid={`emotion-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <BodyText className="text-[#1D6B6B] flex-1">
                                                {option.label}
                                            </BodyText>
                                            {selectedEmotion?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#FFBB98]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="p-4 rounded-xl bg-white/70/60 border border-white/40/40 mb-8">
                            <SmallText className="text-[#7D8E95] leading-relaxed text-center">
                                These softer feelings are often what the cycle is protecting.
                            </SmallText>
                        </div>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton 
                                onClick={() => setStep('need')} 
                                className="flex-1" 
                                size="lg"
                                disabled={!selectedEmotion}
                            >
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* WHAT I NEED (PRIVATE - will be shared) */}
                {step === 'need' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="sage" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Hand className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                What do you need right now?
                            </Heading>
                        </div>

                        <div className="p-3 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mb-6 text-center">
                            <SmallText className="text-[#1D6B6B]">
                                This will be shared gently with your partner
                            </SmallText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {needOptions.map((option, index) => {
                                const cardVariants = ['glass-card-sage', 'glass-card-rose', 'glass-card-lavender', 'glass-card-gold', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedNeed?.id === option.id ? 'ring-2 ring-[#1D6B6B]' : ''}`}
                                        style={{ animationDelay: `${index * 50}ms` }}
                                        onClick={() => handleNeedSelect(option)}
                                        data-testid={`need-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <BodyText className="text-[#1D6B6B] flex-1">
                                                {option.label}
                                            </BodyText>
                                            {selectedNeed?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#1D6B6B]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="p-4 rounded-xl bg-white/70/60 border border-white/40/40 mb-8">
                            <SmallText className="text-[#7D8E95] leading-relaxed text-center">
                                It's okay if you're unsure.
                            </SmallText>
                        </div>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton 
                                onClick={handleShareWithPartner} 
                                className="flex-1" 
                                size="lg"
                                disabled={!selectedNeed}
                                data-testid="share-with-partner"
                            >
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* WAITING FOR PARTNER TO SHARE */}
                {step === 'waiting_partner' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <div 
                                className={`w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center ${
                                    !partnerConnected && partnerCanReconnect 
                                        ? 'bg-[#FFBB98]/20' 
                                        : 'bg-[#7D8E95]/20'
                                }`}
                                style={{ animation: 'pulse-soft 2s ease-in-out infinite' }}
                            >
                                {!partnerConnected && partnerCanReconnect ? (
                                    <RefreshCw className="w-8 h-8 text-[#B8A070]" />
                                ) : (
                                    <Users className="w-8 h-8 text-[#7D8E95]" />
                                )}
                            </div>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                {!partnerConnected && partnerCanReconnect 
                                    ? `Waiting for ${partnerName} to reconnect`
                                    : `Waiting for ${partnerName}`
                                }
                            </Heading>
                            <BodyText className="text-[#2A8585]">
                                {!partnerConnected && partnerCanReconnect 
                                    ? `They have ${reconnectGraceMinutes} minutes to rejoin.`
                                    : 'Your partner is still reflecting. Give them a moment.'
                                }
                            </BodyText>
                        </div>

                        {/* Partner disconnection notice */}
                        {!partnerConnected && partnerCanReconnect && (
                            <div className="p-4 rounded-xl bg-[#FFBB98]/20 border border-white/40/30 mb-6 text-center">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <RefreshCw className="w-4 h-4 text-[#B8A070]" />
                                    <SmallText className="text-[#8A7A60] font-medium">
                                        Partner disconnected
                                    </SmallText>
                                </div>
                                <SmallText className="text-[#8A7A60]">
                                    Don't worry — they can rejoin and continue where you left off.
                                </SmallText>
                            </div>
                        )}

                        <div className="glass-card rounded-2xl p-5">
                            <SmallText className="text-[#7D8E95] mb-2 block text-center">What you'll share:</SmallText>
                            <BodyText className="text-[#2A8585] text-center">
                                "{partnerName}, I'm feeling <span className="text-[#FFBB98] font-medium">{selectedEmotion?.shareLabel}</span> and I need <span className="text-[#1D6B6B] font-medium">{selectedNeed?.shareLabel}</span>."
                            </BodyText>
                        </div>

                        <div className="p-4 rounded-xl bg-white/70/60 border border-white/40/40 text-center">
                            <SmallText className="text-[#7D8E95]">
                                This is a moment to breathe. Your partner is doing their own reflection.
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* WHAT GETS SHARED (SHARED SCREEN) */}
                {step === 'received' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                {partnerName} shared with you
                            </Heading>
                        </div>

                        <div className="glass-card rounded-2xl p-5">
                            <BodyText className="text-[#1D6B6B] text-center text-lg leading-relaxed">
                                "Your partner is feeling <span className="text-[#FFBB98] font-medium">{partnerEmotion}</span> and needs <span className="text-[#1D6B6B] font-medium">{partnerNeed}</span>."
                            </BodyText>
                        </div>

                        <PrimaryButton
                            onClick={() => setStep('respond')}
                            className="w-full"
                            size="xl"
                            data-testid="respond-to-partner"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </PrimaryButton>
                    </div>
                )}

                {/* GUIDED PARTNER RESPONSE (PRIVATE) */}
                {step === 'respond' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="lavender" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                Respond to {partnerName}
                            </Heading>
                        </div>

                        <div className="space-y-3 mb-6">
                            {responseOptions.map((option, index) => {
                                const cardVariants = ['glass-card-lavender', 'glass-card-rose', 'glass-card-sage', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedResponse?.id === option.id ? 'ring-2 ring-[#7D8E95]' : ''}`}
                                        style={{ animationDelay: `${index * 60}ms` }}
                                        onClick={() => handleSendResponse(option)}
                                        data-testid={`response-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <BodyText className="text-[#1D6B6B] flex-1 italic">
                                                "{option.label}"
                                            </BodyText>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="p-4 rounded-xl bg-white/70/60 border border-white/40/40 text-center">
                            <SmallText className="text-[#7D8E95]">
                                Choose the response that feels true for you.
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* CO-REGULATION MOMENT (SHARED) */}
                {step === 'coregulate' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <div 
                                className="w-20 h-20 mx-auto mb-5 rounded-full bg-[#7D8E95]/30 flex items-center justify-center"
                                style={{ animation: 'breathe 4s ease-in-out infinite' }}
                            >
                                <Wind className="w-8 h-8 text-[#7D8E95]" />
                            </div>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                Slow Together
                            </Heading>
                        </div>

                        {/* Show partner's response if received */}
                        {partnerResponse && (
                            <div className="glass-card rounded-2xl p-5">
                                <SmallText className="text-[#7D8E95] mb-1 block text-center">
                                    {partnerName} responded:
                                </SmallText>
                                <BodyText className="text-[#2A8585] text-center italic">
                                    "{partnerResponse}"
                                </BodyText>
                            </div>
                        )}

                        <div className="space-y-3 mb-8">
                            {groundingOptions.map((option, index) => {
                                const IconComponent = option.icon;
                                const cardVariants = ['glass-card-sage', 'glass-card-rose', 'glass-card-lavender'];
                                const iconGradients = [
                                    'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)',
                                    'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)',
                                    'linear-gradient(145deg, #E8A0A0 0%, #C97B7B 100%)'
                                ];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedGrounding?.id === option.id ? 'ring-2 ring-[#7D8E95]' : ''}`}
                                        style={{ animationDelay: `${index * 80}ms` }}
                                        onClick={() => handleGroundingSelect(option)}
                                        data-testid={`grounding-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div 
                                                className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
                                                style={{ background: iconGradients[index % iconGradients.length], color: 'white' }}
                                            >
                                                <IconComponent className="w-5 h-5" />
                                            </div>
                                            <BodyText className="text-[#1D6B6B] flex-1">
                                                {option.label}
                                            </BodyText>
                                            {selectedGrounding?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#7D8E95]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Show what partner chose */}
                        {partnerGrounding && (
                            <div className="p-4 rounded-xl bg-white/70/60 border border-white/40/40 mb-6 text-center">
                                <SmallText className="text-[#7D8E95]">
                                    {partnerName} chose: {groundingOptions.find(g => g.id === partnerGrounding)?.label}
                                </SmallText>
                            </div>
                        )}

                        <PrimaryButton
                            onClick={handleComplete}
                            className="w-full"
                            size="xl"
                            data-testid="complete-coregulation"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </PrimaryButton>
                    </div>
                )}

                {/* RECONNECTION SCREEN (SHARED) */}
                {step === 'reconnect' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Sparkles className="w-6 h-6" />
                            </IconContainer>
                        </div>

                        <div className="glass-card rounded-2xl p-5">
                            <BodyText className="text-[#1D6B6B] leading-relaxed text-center text-lg">
                                When both people slow down, name what's happening inside, and respond with softness, the cycle loses its power. Even in hurt or fear, it becomes possible to turn toward each other and feel met.
                            </BodyText>
                        </div>

                        {/* Optional prompts */}
                        <div className="p-5 rounded-2xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mb-8">
                            <SmallText className="text-[#7D8E95] font-medium mb-3 block text-center">
                                Optional prompts:
                            </SmallText>
                            <div className="space-y-3">
                                <div className="p-3 rounded-xl bg-white/50">
                                    <SmallText className="text-[#2A8585] text-center italic block">
                                        "What's one small thing you appreciate about each other right now?"
                                    </SmallText>
                                </div>
                                <div className="p-3 rounded-xl bg-white/50">
                                    <SmallText className="text-[#2A8585] text-center italic block">
                                        "What's one thing you want the other to know?"
                                    </SmallText>
                                </div>
                            </div>
                        </div>

                        <PrimaryButton
                            onClick={handleClose}
                            className="w-full"
                            size="xl"
                            data-testid="finish-btn"
                        >
                            Finish
                        </PrimaryButton>

                        <div className="mt-8 text-center">
                            <SmallText className="text-[#7D8E95]">
                                You can return to Partner Mode anytime you need to reconnect.
                            </SmallText>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default PartnerMode;
